import React, { useState } from 'react';
import { loginUser } from '../services/api';
import '../styles/Login.css';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`📝 Field changed: ${name}`);
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDismissError = () => {
    console.log('✖️ User dismissed error');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('\n═══════════════════════════════════════');
    console.log('🔐 LOGIN ATTEMPT');
    console.log('═══════════════════════════════════════');
    
    // Clear only error, NOT form fields
    setError('');
    
    const username = formData.username.trim();
    const password = formData.password.trim();
    
    console.log('📋 Current form state:');
    console.log('   Username:', username);
    console.log('   Password length:', password.length);
    
    // Validation
    if (!username) {
      console.log('❌ Validation: Username empty');
      setError('Please enter your username');
      return;
    }

    if (!password) {
      console.log('❌ Validation: Password empty');
      setError('Please enter your password');
      return;
    }

    // Start API call
    setLoading(true);
    console.log('⏳ Loading: TRUE');
    console.log('🌐 Calling API...');

    try {
      const response = await loginUser(username, password);
      console.log('📨 Response:', response);
      
      if (response.success) {
        // ✅✅✅ SUCCESS ✅✅✅
        console.log('╔════════════════════════════════════╗');
        console.log('║  ✅ LOGIN SUCCESSFUL! ✅           ║');
        console.log('╚════════════════════════════════════╝');
        
        setError('');
        
        // 🔒🔒🔒 DO NOT CLEAR FIELDS - PER USER REQUEST 🔒🔒🔒
        // Fields stay filled even on success
        console.log('🔒 FIELDS PRESERVED (not clearing)');
        console.log('📌 Username remains:', formData.username);
        console.log('📌 Password remains: [hidden]');
        
        // NO setFormData({ username: '', password: '' });
        // Fields stay as-is!
        
        console.log('🎯 Calling onLoginSuccess...');
        onLoginSuccess(response.data);
        
      } else {
        // ❌❌❌ ERROR ❌❌❌
        console.log('╔════════════════════════════════════╗');
        console.log('║  ❌ LOGIN FAILED! ❌               ║');
        console.log('╚════════════════════════════════════╝');
        
        const errorMessage = response.message || 
          'Invalid username or password. Please check your credentials and try again.';
        
        console.log('🔴 Error:', errorMessage);
        setError(errorMessage);
        
        console.log('🔒 FIELDS PRESERVED (not clearing)');
        console.log('📌 Username remains:', formData.username);
        console.log('📌 Password remains: [hidden]');
      }
      
    } catch (err) {
      // ❌❌❌ EXCEPTION ❌❌❌
      console.log('╔════════════════════════════════════╗');
      console.log('║  ⚠️ EXCEPTION! ⚠️                  ║');
      console.log('╚════════════════════════════════════╝');
      console.error('💥 Exception:', err);
      
      const errorMessage = err.message || 
        'Unable to connect to server. Please check your internet connection and try again.';
      
      console.log('🔴 Error:', errorMessage);
      setError(errorMessage);
      
      console.log('🔒 FIELDS PRESERVED (not clearing)');
      console.log('📌 Username remains:', formData.username);
      console.log('📌 Password remains: [hidden]');
      
    } finally {
      setLoading(false);
      console.log('⏳ Loading: FALSE');
      console.log('═══════════════════════════════════════\n');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>LTMS</h1>
          <p>Logistics and Transport Management System</p>
        </div>

        {/* ERROR MESSAGE - PERMANENT UNTIL DISMISSED */}
        {error && (
          <div className="error-message-wrapper">
            <div className="error-message" role="alert" aria-live="assertive">
              <div className="error-content">
                <span className="error-icon">⚠️</span>
                <div className="error-text-wrapper">
                  <div className="error-text">{error}</div>
                  <div className="error-hint">
                    {error.includes('username') || error.includes('password') 
                      ? 'Fill in the required field to continue'
                      : 'Click × to dismiss, or correct your credentials and try again'
                    }
                  </div>
                </div>
              </div>
              <button 
                type="button"
                className="error-dismiss"
                onClick={handleDismissError}
                aria-label="Dismiss error"
                title="Click to dismiss"
              >
                ×
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" noValidate autoComplete="on">
          {/* Username Field */}
          <div className="form-group">
            <label htmlFor="username">
              Username <span className="required">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              disabled={loading}
              autoComplete="username"
              autoFocus
              className={error ? 'input-has-error' : ''}
            />
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password">
              Password <span className="required">*</span>
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                className={error ? 'input-has-error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <span className="button-loading">
                <span className="loading-spinner"></span>
                <span>Logging in...</span>
              </span>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>&copy; 2024 LTMS. All rights reserved.</p>
        </div>
      </div>
      
      {/* DEBUG PANEL - Development Only */}
      {process.env.NODE_ENV === 'development' && (
        <div className="debug-panel">
          <div className="debug-title">🐛 Debug Info</div>
          
          <div className="debug-row">
            <span className="debug-label">Loading:</span>
            <span className={`debug-value ${loading ? 'debug-true' : 'debug-false'}`}>
              {loading ? '✓ TRUE' : '✗ FALSE'}
            </span>
          </div>
          
          <div className="debug-row">
            <span className="debug-label">Has Error:</span>
            <span className={`debug-value ${error ? 'debug-true' : 'debug-false'}`}>
              {error ? '✓ YES' : '✗ NO'}
            </span>
          </div>
          
          <div className="debug-row">
            <span className="debug-label">Username:</span>
            <span className="debug-value debug-info">
              {formData.username ? `"${formData.username}"` : '(empty)'}
            </span>
          </div>
          
          <div className="debug-row">
            <span className="debug-label">Password:</span>
            <span className="debug-value debug-info">
              {formData.password ? `${formData.password.length} chars` : '(empty)'}
            </span>
          </div>
          
          {error && (
            <>
              <div className="debug-divider"></div>
              <div className="debug-row">
                <span className="debug-label">Error:</span>
                <span className="debug-value debug-error">
                  {error.substring(0, 40)}...
                </span>
              </div>
            </>
          )}
          
          <div className="debug-footer">
            ℹ️ Fields NEVER clear (per user request)
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;